import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { UomConversionStatus } from 'companySettings/enums';
import { IUomConversionAuditRow } from 'companySettings/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { StatusCell } from 'core/elements';
import { PartNumberHelper } from 'parts/helpers';
import { Color } from 'theme/enums';
import { IColorMap } from 'theme/types';

import { UomConversionAuditColId } from './enums';
import { PartNumberCell } from './PartNumberCell';
import { QuoteIdsCell } from './QuoteIdsCell';
import { getUomConversionStatusLabels } from '../utils';

// missing = the actionable problem; the no_* statuses = data gaps that explain why a
// conversion can't be classified yet; exists / same_as_base = nothing to do.
const conversionStatusColorMap: IColorMap = {
  [UomConversionStatus.EXISTS]: Color.SUCCESS,
  [UomConversionStatus.MISSING]: Color.DANGER,
  [UomConversionStatus.NO_BASE_UOM]: Color.WARNING,
  [UomConversionStatus.NO_CORPORATE_PART]: Color.WARNING,
  [UomConversionStatus.NO_EXTRACTED_UOM]: Color.WARNING,
  [UomConversionStatus.SAME_AS_BASE]: Color.NEUTRAL,
};

// Leaf rows show the value or the `—` placeholder; group rows (where `data` is
// undefined) stay blank, mirroring the sibling VendorQuoteLog table.
const formatWithPlaceholder = ({ data, value }: ValueFormatterParams) =>
  data ? value || DEFAULT_PLACEHOLDER : value;

// The corporate-part UOM columns are identical apart from identity and sizing; the
// colId enum values double as the row field names.
const uomTextColumn = (
  colId: UomConversionAuditColId,
  headerName: string,
  sizing: Pick<ColDef, 'minWidth' | 'width'>
): ColDef => ({
  colId,
  headerName,
  field: colId,
  filter: 'agTextColumnFilter',
  enableRowGroup: false,
  valueFormatter: formatWithPlaceholder,
  ...sizing,
});

export const columnDef = (onPartClick: (row: IUomConversionAuditRow) => void): ColDef[] => {
  const statusLabels = getUomConversionStatusLabels();

  return [
    {
      colId: UomConversionAuditColId.PART_NUMBER,
      headerName: t('headerPartNumber'),
      field: 'partNumber',
      filter: 'agTextColumnFilter',
      enableRowGroup: false,
      minWidth: 160,
      // Display, filter, sort and export all read the same formatted label the part
      // details panel shows, so an underscored part number cannot read two ways.
      valueGetter: ({ data }) => PartNumberHelper.toLabel(data?.partNumber),
      cellRenderer: PartNumberCell,
      cellRendererParams: { onPartClick },
    },
    {
      colId: UomConversionAuditColId.PART_DESCRIPTION,
      headerName: t('headerDescription'),
      field: 'partDescription',
      filter: 'agTextColumnFilter',
      enableRowGroup: false,
      // Descriptions run long enough to push every other column off-screen, so the
      // column is capped and the text wraps over a few lines instead. maxRows is
      // the shared cell renderer's own clamp -- `wrapText` alone does nothing here
      // because that renderer puts the value in a Typography that ellipsises at one
      // line. autoHeight then grows the row to whatever the clamp needs, as Quote
      // IDs already does below, and the renderer keeps a tooltip for the full text.
      minWidth: 200,
      maxWidth: 400,
      autoHeight: true,
      cellRendererParams: { maxRows: 3 },
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: UomConversionAuditColId.MANUFACTURER,
      headerName: t('headerManufacturer'),
      field: 'manufacturer',
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      minWidth: 150,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: UomConversionAuditColId.EXTRACTED_UOM,
      headerName: t('headerExtractedUom'),
      field: 'extractedUom',
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      width: 140,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: UomConversionAuditColId.STATUS,
      headerName: t('headerStatus'),
      field: 'conversionToBase',
      // Set filter over the raw status values: this is the page's status filter
      // (seeded to `missing` via the table's initialState).
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => statusLabels[value],
      },
      enableRowGroup: true,
      minWidth: 170,
      cellRenderer: StatusCell,
      cellRendererParams: { colorMap: conversionStatusColorMap, labelMap: statusLabels },
      // The cell renders via StatusCell; the formatter feeds XLSX/CSV export and
      // clipboard so they carry the label, not the raw enum value.
      valueFormatter: ({ value }) => value && statusLabels[value],
    },
    {
      colId: UomConversionAuditColId.LINE_COUNT,
      headerName: t('headerFrequency'),
      field: 'lineCount',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      width: 100,
    },
    {
      colId: UomConversionAuditColId.QUOTE_IDS,
      headerName: t('headerQuoteIds'),
      field: 'quoteIds',
      filter: 'agTextColumnFilter',
      enableRowGroup: false,
      minWidth: 160,
      // Flattened to text so filtering, sorting and the exports all act on what
      // the cell shows rather than on the array; the renderer draws the pills.
      valueGetter: ({ data }) => data?.quoteIds?.join(', ') ?? '',
      valueFormatter: formatWithPlaceholder,
      cellRenderer: QuoteIdsCell,
      autoHeight: true,
    },
    {
      colId: UomConversionAuditColId.BASE_UOM,
      headerName: t('headerBaseUom'),
      field: 'baseUom',
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      width: 130,
      // The resolved UOM name when the BE matched one, otherwise the raw stored text.
      valueGetter: ({ data }) => data && (data.baseUom || data.baseUomText),
      valueFormatter: formatWithPlaceholder,
    },
    uomTextColumn(UomConversionAuditColId.SKU_UOM, t('headerSkuUoms'), { minWidth: 130 }),
    uomTextColumn(UomConversionAuditColId.SALES_UOM, t('headerSalesUom'), { width: 130 }),
    uomTextColumn(UomConversionAuditColId.PURCHASE_COST_UOM, t('headerPurchaseCostUom'), {
      width: 150,
    }),
    uomTextColumn(UomConversionAuditColId.EXTERNAL_UOM, t('headerExternalUom'), { width: 140 }),
    uomTextColumn(UomConversionAuditColId.PACKAGE_UOM, t('headerPackageUom'), { width: 140 }),
    uomTextColumn(UomConversionAuditColId.ALTERNATE_UOM, t('headerAlternateUom'), { width: 150 }),
    uomTextColumn(UomConversionAuditColId.ALTERNATE_SALES_UOM, t('headerAlternateSalesUom'), {
      width: 170,
    }),
  ];
};
